<script setup lang="ts">
import { computed, onMounted, onUnmounted, reactive, watch } from 'vue'
import { authorize } from '@/shared/lib/auth/authorize'
import { checkAuthState } from '@/shared/lib/auth/checkAuth'
import { ensureValidToken } from '@/shared/lib/auth/tokenManager'
import { getTelegramInitData } from '@/shared/lib/auth/telegramAuth'
import { useUserStore } from '@/stores/useUser'
import { useSubscriptionStore } from '@/entities/Subscription/model/subscriptionStore'
import { usePaymentStore } from '@/entities/Payment/model/paymentStore'
import MainLayout from '@/ui/layouts/MainLayout.vue'
import Loader from '@/ui/components/Loader.vue'
import ModalContainer from '@/ui/components/Modal/ModalContainer.vue'
import LoginPage from '@/pages/Auth/LoginPage.vue'
import { setJivoGuestMode, syncJivoUserContact } from '@/shared/lib/jivo/initJivoWidget'
import { useRoute } from 'vue-router'

const userStore = useUserStore()
const subscriptionStore = useSubscriptionStore()
const paymentStore = usePaymentStore()
const route = useRoute()

type AuthView = 'loading' | 'login' | 'app'

const state = reactive({
  currentView: 'loading' as AuthView,
  bootstrapLoading: false,
})

let tokenCheckInterval: number | null = null

const startTokenValidation = () => {
  stopTokenValidation()
  tokenCheckInterval = window.setInterval(async () => {
    if (userStore.isAuthenticated()) {
      try {
        await ensureValidToken()
      } catch (error) {
        console.error('Ошибка при проверке токена:', error)
      }
    }
  }, 5 * 60 * 1000)
}

const stopTokenValidation = () => {
  if (tokenCheckInterval) {
    clearInterval(tokenCheckInterval)
    tokenCheckInterval = null
  }
}

const showLoginView = () => {
  stopTokenValidation()
  state.currentView = 'login'
}

const loadAppData = async () => {
  state.bootstrapLoading = true
  try {
    await userStore.fetchUserInfo()

    await subscriptionStore.fetchSubscription(false)

    if (subscriptionStore.subscription?.id) {
      await paymentStore.fetchTariffs(subscriptionStore.subscription.id)
    } else {
      await paymentStore.fetchTariffs()
    }

    state.currentView = 'app'
    startTokenValidation()
  } catch (error) {
    console.error('Ошибка загрузки данных приложения:', error)
    showLoginView()
  } finally {
    state.bootstrapLoading = false
  }
}

const initializeApp = async () => {
  state.currentView = 'loading'

  try {
    const isAlreadyAuth = checkAuthState()

    if (isAlreadyAuth) {
      const isTokenValid = await ensureValidToken()
      if (isTokenValid && userStore.isAuthenticated()) {
        await loadAppData()
        return
      }
    }

    const { initData } = getTelegramInitData()

    if (initData) {
      await authorize()
      if (userStore.isAuthenticated()) {
        await loadAppData()
        return
      }
    }

    showLoginView()
  } catch (error) {
    console.error('Ошибка инициализации приложения:', error)
    showLoginView()
  }
}

const handleLoginSuccess = async () => {
  await loadAppData()
}

const shouldRenderPublicPage = computed(
  () => route.name === 'policy' || route.name === 'static-pay'
)

const shouldShowLoader = computed(() => {
  if (shouldRenderPublicPage.value) {
    return false
  }

  if (state.currentView === 'loading') {
    return true
  }

  if (state.bootstrapLoading) {
    return true
  }
  return false
})

onMounted(async () => {
  if (!shouldRenderPublicPage.value) {
    await initializeApp()
  }
})

onUnmounted(() => {
  stopTokenValidation()
})

watch(
  () => userStore.user,
  (user) => {
    setJivoGuestMode(!user)
    syncJivoUserContact(user)
  },
  { immediate: true },
)

watch(
  () => (userStore.user ? userStore.user.tg_id : null),
  (tgId) => {
    if (state.currentView === 'loading') {
      return
    }

    if (tgId === null && state.currentView === 'app') {
      showLoginView()
    }
  }
)

watch(
  () => route.name,
  async () => {
    if (shouldRenderPublicPage.value) {
      stopTokenValidation()
      return
    }

    if (state.currentView === 'loading') {
      await initializeApp()
    }
  }
)

</script>

<template>
  <Loader v-if="shouldShowLoader" />

  <MainLayout v-else>
    <template #content>
      <LoginPage
        v-if="state.currentView === 'login' && !shouldRenderPublicPage"
        @login-success="handleLoginSuccess"
      />
      <KeepAlive v-else>
        <RouterView />
      </KeepAlive>
    </template>
  </MainLayout>

  <ModalContainer />
</template>

<style lang="scss">
</style>
